import React, { forwardRef, useImperativeHandle } from 'react';
import { useFetchUserSessionsQuery } from '../../../services/firebaseApiSlice';
import './SessionsList.css';

interface ThemeColors {
  backgroundPrimary?: string;
  backgroundSecondary?: string;
  textPrimary?: string;
  textSecondary?: string;
  shadowColor?: string;
  [key: string]: unknown;
}

interface SessionsListProps {
  userEmail?: string;
  onSessionClick?: (session: unknown) => void;
  theme: ThemeColors;
}

interface Session {
  id?: string;
  thread_id?: string;
  title?: string;
  agent_type?: string;
  notification_type?: string;
  last_updated?: string;
  messages?: unknown[];
  Progress_status?: boolean;
  unhandled?: boolean;
  awaiting_approval?: boolean;
  first_node_done?: boolean;
  date_created?: string | number;
  created_at?: string | number;
}

const toMillis = (value: unknown): number => {
    if (value instanceof Date) {
        const ms = value.getTime();
        return Number.isNaN(ms) ? 0 : ms;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        return value < 1e12 ? value * 1000 : value;
    }

    if (typeof value === 'string') {
        const asNumber = Number(value);
        if (!Number.isNaN(asNumber)) {
            return asNumber < 1e12 ? asNumber * 1000 : asNumber;
        }
        const parsed = new Date(value).getTime();
        return Number.isNaN(parsed) ? 0 : parsed;
    }

    if (value && typeof value === 'object') {
        const maybeTimestamp = value as {
            toDate?: () => Date;
            seconds?: number | string;
            nanoseconds?: number | string;
            _seconds?: number | string;
            _nanoseconds?: number | string;
            sec?: number | string;
            nsec?: number | string;
        };

        if (typeof maybeTimestamp.toDate === 'function') {
            return maybeTimestamp.toDate().getTime();
        }

        const rawSeconds = maybeTimestamp.seconds ?? maybeTimestamp._seconds ?? maybeTimestamp.sec;
        const rawNanos = maybeTimestamp.nanoseconds ?? maybeTimestamp._nanoseconds ?? maybeTimestamp.nsec;
        const seconds = Number(rawSeconds);
        const nanos = Number(rawNanos ?? 0);

        if (Number.isFinite(seconds)) {
            const safeNanos = Number.isFinite(nanos) ? nanos : 0;
            return seconds * 1000 + Math.floor(safeNanos / 1e6);
        }
    }

    return 0;
};

const getSessionSortKey = (session: Session): number =>
    toMillis(session.date_created) ||
    toMillis(session.created_at) ||
    toMillis(session.last_updated);

const formatSessionDate = (session: Session): string => {
    const timestamp = getSessionSortKey(session);
    if (!timestamp) {
        return '—';
    }
    return new Date(timestamp).toLocaleDateString();
};

const isTruthy = (value: unknown): boolean => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
    }

    if (typeof value === 'number') {
        return value === 1;
    }

    return false;
};

const isPlanMessageAwaitingApproval = (session: Session): boolean => {
    if (session.agent_type !== "PlanMessageAgent") {
        return false;
    }
    if (isTruthy(session.awaiting_approval)) {
        return true;
    }
    if (session.notification_type === "plan_message_schedule_approval") {
        return true;
    }
    return isTruthy(session.unhandled);
};

const SessionsList = forwardRef<{ loadSessions: () => Promise<unknown> }, SessionsListProps>(({ userEmail: userId, onSessionClick, theme }, ref) => {
    const { data: sessions = [], isLoading: loading, error, refetch } = useFetchUserSessionsQuery(
        { userEmail: userId! },
        {
            skip: !userId,
        }
        
    );

    useImperativeHandle(ref, () => ({
        loadSessions: async () => {
            try {
                const result = await refetch();
                console.log('SessionsList: Refetch result:', result);
                return result;
            } catch (err) {
                console.error('❌ SessionsList: Refetch error:', err);
                throw err;
            }
        }
    }));

    const themeStyles: React.CSSProperties & Record<string, string> = {
        '--session-item-bg': theme?.backgroundPrimary || '#f5f5f5',
        '--session-item-hover-bg': theme?.backgroundSecondary || '#e8e8e8',
        '--session-item-shadow': `0 1px 2px ${theme?.shadowColor || 'rgba(0, 0, 0, 0.05)'}`,
        '--session-id-color': theme?.textPrimary || '#333333',
        '--session-type-color': theme?.textSecondary || '#666666',
        '--session-meta-color': theme?.textSecondary || '#666666',
        '--session-state-color': theme?.textSecondary || '#666666',
        '--session-incomplete-bg': 'rgba(255, 68, 68, 0.08)',
        '--session-incomplete-border': '#ff4444',
        '--session-error-color': '#ff4444',
        '--session-unhandled-bg': 'rgba(173, 216, 230, 0.15)',
        '--session-unhandled-border': '#87CEEB',
        '--session-awaiting-approval-bg': 'rgba(255, 68, 68, 0.1)',
        '--session-awaiting-approval-border': '#ff4444',
    };

    if (loading) {
        return <div className='sessions-loading' style={themeStyles}>Loading sessions...</div>;
    }

    if (error) {
        return <div className='sessions-error' style={themeStyles}>Failed to load sessions: {(error as { message?: string })?.message || 'Unknown error'}</div>;
    }

    if (!(sessions as unknown[]).length) {
        return <div className='sessions-empty' style={themeStyles}>No sessions found</div>;
    }

    const sortedSessions = [...(sessions as Session[])].sort((a, b) => getSessionSortKey(b) - getSessionSortKey(a));

    return (
        <div className='sessions-list-container' style={themeStyles}>
            <div className='sessions-list'>
                {sortedSessions.map((session) => {
                    const isInComplete = session.Progress_status === false;
                    const isUnhandled = isTruthy(session.unhandled);
                    const awaitingApproval = isPlanMessageAwaitingApproval(session);
                    const needsResponse = isUnhandled && !awaitingApproval;

                    return (
                        <div
                            key={session.id}
                            className={`session-item ${isInComplete ? 'session-incomplete' : ''} ${awaitingApproval ? 'session-awaiting-approval' : ''} ${needsResponse ? 'session-unhandled' : ''}`}
                            onClick={() => {
                                onSessionClick && onSessionClick(session);
                            }}
                        >
                            <div className='session-header'>
                                <span className='session-id'>{session.title}...</span>
                                <span className='session-type'>{session.agent_type}</span>
                                {isInComplete && (
                                    <span className='session-status-indicator'>In Progress</span>
                                )}
                                {awaitingApproval && (
                                    <span className='session-awaiting-approval-indicator'>Awaiting Approval</span>
                                )}
                                {needsResponse && (
                                    <span className='session-unhandled-indicator'>Needs Response</span>
                                )}
                            </div>
                            <div className='session-timestamp'>
                                {formatSessionDate(session)}
                            </div>
                            <div className='session-messages'>
                                {session.messages?.length || 0} msgs
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

SessionsList.displayName = 'SessionsList';

export default SessionsList;
